/**
 * Turns a worker agent's raw tool call into the ONE human sentence a person would use for it — no tool
 * names, no params, no paths-as-code. This is what the "View Agents Conversation" transcript shows for a
 * TOOL_USE row, so the reader watches the agent *act* ("Updating the log", "Sending a WhatsApp message",
 * "Reporting back to the Maestro") instead of reading a machine log or seeing nothing at all.
 *
 * Contract: given a tool name and its (already-parsed or raw-JSON) input, return a short present-continuous
 * line, or `null` to HIDE the row entirely — pure bookkeeping (marking a plan step, updating the task card)
 * is noise to a human and collapses out. The client renders non-null lines as a muted "· doing X" step
 * between the agent's spoken turns.
 *
 * Shared 1:1 with the native ports (iOS `AgentActionLine`, Android `agentActionLine`) — keep the mapping,
 * the hidden set, and the fallback identical across the three, since all three render the same transcript.
 */

function parseInput(raw) {
  if (raw && typeof raw === 'object') {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const v = JSON.parse(raw);
      return v && typeof v === 'object' ? v : {};
    } catch {
      return {};
    }
  }
  return {};
}
function str(input, key) {
  const v = input[key];
  return typeof v === 'string' ? v.trim() : '';
}

/** The bare filename a human recognizes — the last path segment, extension dropped. */
function fileLabel(path) {
  const base = path.split('/').filter(Boolean).pop() ?? path;
  return base.replace(/\.[a-z0-9]+$/i, '') || 'a file';
}
const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
// "outreach_id: `<uuid>`" / "recipient_id = <uuid>" / "(conversationId: <uuid>)" — a raw identifier the
// agent wrote into a sentence it thought only a machine would read. The label + the id go together.
const LABELLED_ID = new RegExp(`[(\\[]?\\s*\`?\\b[\\w-]*id\`?\\s*[:=]\\s*\`?${UUID}\`?\\s*[)\\]]?`, 'gi');
// Any UUID left after the labelled pass — a bare id dropped into prose.
const BARE_UUID = new RegExp(`\`?${UUID}\`?`, 'g');

/**
 * A worker's spoken turn or step narration, cleaned for a human reader. Strips the raw identifiers the
 * agent sometimes appends to a message meant for a person ("…sent. outreach_id: `b42f2…`") — those are
 * machine bookkeeping, never something a user should see — and tidies the punctuation the removal leaves
 * behind. Shared 1:1 with the native ports (iOS `sanitizeAgentText`, Android `sanitizeAgentText`); every
 * surface renders the same transcript, so the cleaning must be identical.
 */
export function sanitizeAgentText(text) {
  if (!text) {
    return '';
  }
  const out = text.replace(LABELLED_ID, '').replace(BARE_UUID, '').replace(/[ \t]{2,}/g, ' ').replace(/[ \t]+([.,;:!?])/g, '$1').replace(/[ \t]*\n[ \t]*/g, '\n')
  // trailing junk the id-removal can leave dangling — a lone backtick, bullet, dash, colon, open paren
  .replace(/[·\-–—`([:;,\s]+$/g, '').trim();
  return out;
}

/** A short, quote-wrapped preview of user-authored content for an action line — never an id or a param. */
function quotePreview(s) {
  const clean = sanitizeAgentText(s).replace(/\s+/g, ' ').trim();
  if (!clean) {
    return '';
  }
  const capped = clean.length > 80 ? `${clean.slice(0, 80)}…` : clean;
  return `“${capped}”`;
}

/**
 * ManageOutreaches: a `create` is the agent SENDING — name the recipient and quote a single short message
 * so the reader sees what actually went out; other actions (a reply, a status flip, a query) read quieter.
 */
function outreachLine(i) {
  const action = str(i, 'action');
  const list = Array.isArray(i.outreaches) ? i.outreaches : [];
  const first = list[0] ?? {};
  const name = str(first, 'recipient_display_name');
  if (action === 'create') {
    if (list.length > 1) {
      return `Sending messages to ${list.length} people`;
    }
    const to = name ? ` to ${name}` : '';
    const msg = quotePreview(str(first, 'message'));
    return msg ? `Sending a message${to}: ${msg}` : `Sending a message${to}`;
  }
  if (action === 'reply') {
    return name ? `Replying to ${name}` : 'Replying to a message';
  }
  if (action === 'complete' || action === 'update_status') {
    return null;
  } // triage bookkeeping — noise to a human. `update_status` is the retired spelling, still in old rows.
  return 'Checking the outreach';
}

/** Tool → human present-continuous action. `null` = a real action with no good phrasing → generic line. */
const ACTIONS = {
  Write: i => `Updating ${fileLabel(str(i, 'path'))}`,
  Edit: i => `Editing ${fileLabel(str(i, 'path'))}`,
  Read: () => 'Reading the notes',
  Glob: () => 'Searching the notes',
  Grep: () => 'Searching the notes',
  LS: () => 'Searching the notes',
  // Logging its own progress is bookkeeping the step narration already tells in the agent's own voice, and
  // the logged line tends to carry raw ids — hide it rather than show a generic "Logging progress" beat.
  LogAgentActions: () => null,
  ManageOutreaches: i => outreachLine(i),
  ManageContact: () => 'Looking up a contact',
  ManageEvent: () => 'Working on the calendar',
  ManageCalendar: () => 'Checking the calendar',
  ManagePulse: () => 'Setting up a routine',
  ManageGoal: () => 'Working on a goal',
  ManageWorkspace: () => 'Organizing a workspace',
  ManageSite: () => 'Checking the connected site',
  SendWhatsapp: i => str(i, 'to') ? `Sending a WhatsApp message to ${str(i, 'to')}` : 'Sending a WhatsApp message',
  SendMessage: () => 'Sending a message',
  ComposioFindTools: () => 'Looking for the right tool',
  // A connector call is the agent acting in someone else's product, on the user's own account — the most
  // consequential thing it does outside Symphony, and until now it rendered as the unknown-tool default,
  // "Working on it". A mailbox send in particular must never be invisible: the user is the sender.
  ComposioExecuteTool: i => {
    const slug = str(i, 'toolSlug').toUpperCase();
    if (!slug) {
      return 'Using a connected app';
    }
    const _slug$split = slug.split('_'),
      toolkit = _slug$split[0];
    const app = toolkit ? toolkit.charAt(0) + toolkit.slice(1).toLowerCase() : '';
    if (/^GMAIL_(SEND_EMAIL|SEND_DRAFT|REPLY_TO_THREAD)$/.test(slug) || /^OUTLOOK_(SEND_EMAIL|REPLY_EMAIL|SEND_DRAFT)$/.test(slug)) {
      return `Sending an email from ${app}`;
    }
    if (/^GMAIL_(CREATE_EMAIL_DRAFT|UPDATE_DRAFT)$/.test(slug) || /^OUTLOOK_CREATE_DRAFT/.test(slug)) {
      return `Writing a draft in ${app}`;
    }
    if (/^(GMAIL|OUTLOOK)_(FETCH|LIST|GET|SEARCH)/.test(slug)) {
      return `Reading ${app}`;
    }
    return app ? `Using ${app}` : 'Using a connected app';
  },
  ManageAgent: i => {
    const action = str(i, 'action');
    if (action === 'report') {
      return 'Reporting back to the Maestro';
    }
    if (action === 'spawn' || action === 'hire') {
      return 'Bringing in another agent';
    }
    if (action === 'review') {
      return 'Reviewing an agent’s work';
    }
    return 'Coordinating with the team';
  }
};

/** ManageTask sub-actions: most are bookkeeping (hidden); a couple are worth showing. */
function manageTaskLine(i) {
  const action = str(i, 'action');
  if (action === 'submit_for_review') {
    return 'Submitting the work for review';
  }
  if (action === 'set_status') {
    const status = str(i, 'status');
    if (status === 'ACTION_NEEDED' || status === 'WAITING') {
      return 'Flagging that it needs you';
    }
    return null; // completed / in-progress flips are bookkeeping
  }
  return null; // update / update_display / create_for_self / query — bookkeeping
}

/**
 * The human action-line for one TOOL_USE row, or `null` to hide it.
 * `toolInput` may be the parsed object or the raw JSON string the wire carries.
 */
export function agentActionLine(toolName, toolInput) {
  const name = (toolName ?? '').trim();
  if (!name) {
    return null;
  }
  const input = parseInput(toolInput);
  if (name === 'ManageTask') {
    return manageTaskLine(input);
  }
  const fn = ACTIONS[name];
  if (fn) {
    return fn(input);
  }
  // Unknown tool: never leak the raw tool name to a person — a neutral "working" beat.
  return 'Working on it';
}
//# sourceMappingURL=agentActionLine.js.map